// /src/components/MainFeed.jsx
//
// ⚠️  CRITICAL COMPONENT - READ ARCHITECTURE DOCUMENTATION BEFORE MODIFYING ⚠️
// This component implements OurNet's v10 dual-context, tri-mode architecture.
// Any changes MUST preserve the two-context (Locality/Groups) and three-mode (News/Chat/Events) system.
// See: /readme/Architecture.md - Section 11: MainFeed Component Architecture
//
// Main feed component implementing v10 architecture with modular header/footer system
// Supports News, Chat, and Events modes with context switching (Locality/Groups)
// Now supports responsive mobile layout (viewport <768px)

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../themes/ThemeProvider';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useGroupGate } from '../hooks/useGroupGate';
import { useGroupClosing } from '../hooks/useGroupClosing';
import { useTranslation } from '../i18n/useTranslation';
import { ArrowLeft } from 'lucide-react';

// Layout Components - Headers and Footers (Desktop only)
import HeaderContainer from './core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsFeed from './core/HeadersAndFooters/HeaderButtonsFeed';
import HeaderButtonsForm from './core/HeadersAndFooters/HeaderButtonsForm';
import FooterContainer from './core/HeadersAndFooters/FooterContainer';
import FooterButtonsFeed from './core/HeadersAndFooters/FooterButtonsFeed';
import HeaderFooterDisplay from './core/HeaderFooterDisplay';

// Sidebar Components (Desktop only). SidebarGroups retired in Groups Tier 1 — the group rail
// now lives in GroupFrontPage's GroupHomeColumn.
import SidebarLocality from './sidebar/SidebarLocality';

// Groups Tier 1: desktop group front page + private-gate. The membership/landing lookups that
// drive the §4 routing gate are in hooks/useGroupGate.
import GroupFrontPage from './frontpage/GroupFrontPage';
import GroupPrivateGate from './groups/GroupPrivateGate';

// Demo Components
import DemoWatermark from './demo/DemoWatermark';

// Mode Grid Components
import NewsGrid from './news/NewsGrid';
import ChatGrid from './chat/ChatGrid';
import EventsGrid from './events/EventsGrid';
import GroupsGrid from './groups/GroupsGrid';

// Locality desktop front page (MultiViewGrid — replaces mode-switched grid for desktop locality)
import LocalityFrontPage from './frontpage/LocalityFrontPage';

// Mobile front page (four full-width screens — replaces mode-switched grid for mobile locality)
import MobileFrontPage from './frontpage/MobileFrontPage';

// Mobile group front page (Approach B — identity card + framed tabs + sheet; the mobile twin
// of GroupFrontPage). Replaces the retired mode-grid + NavigationDrawer path for mobile groups.
import MobileGroupFrontPage from './mobile/MobileGroupFrontPage';

// Mobile Components (MobileHeader removed - HeaderButtonsFeed is now responsive)
import MobileBottomBar from './mobile/MobileBottomBar';

// Modals
import ErrorReportModal from './core/ErrorReportModal';
import StandardButton from './core/StandardButton';

function MainFeed({
  currentContext,
  setCurrentContext,
  articles = [],
  articlesHasMore = false,
  // Load More for the locality News and Events lists (Roadmap #104): null outside the locality
  // context and while searching, so a group's lists never offer it.
  feedPaging = null,
  chats = [],
  events = [],
  onArticleClick,
  onChatClick,
  onNewChat,
  onEventClick,
  onLoadChatsForGroup,
  onLoadArticlesForGroup,
  onLoadEventsForGroup,
  onLoadLocalityArticles,
  onGuestLocationChange,
  currentUser,
  selectedGroup,
  setSelectedGroup,
  setPreviousGroup,      // Tier 2: remembers the group you came FROM, for the gate Back affordance
  onGroupGateBack,       // Tier 2: returns to the previous group's Home tab (or discovery)
  selectedArea,
  setSelectedArea,
  detectedBoundaryId = null,
  visitArea = null,       // active temporary visit (Change 3): { boundaryIds, displayName } | null
  onEnterVisit,
  onClearVisit,           // ends the visit — called before an own-area-slot select (addendum-4 §2)
  currentMode,
  setCurrentMode,
  frontPageActive = true,
  setFrontPageActive,
  mobileScreen = 'news',
  setMobileScreen,
  userHomeLocationName = 'My Location',
  userHomeLocationCoords = null,
  isLoggedIn = false,
  onShowLoginModal = null,
  sessionReady = false,
  groupsInitialMode = 'joined',
  setGroupsInitialMode = null,
  feedScrollPosition = 0,
  shouldRestoreScroll = false,
  onScrollRestored = null,
  articleSearchTerm = '',
  onClearSearch = null,
  // Chat accordion props
  openChatThreadId = null,
  onChatThreadToggle,
  onChatsRefresh,
  isDemoMode = false,     // Phase B1: an appointed demo admin is viewing /demo/* (App.js owns it)
  showDemoWatermark = false // demo mode, or demo content still on screen after leaving it
}) {
  const theme = useTheme();
  const { isMobile, headerHeight, footerHeight } = useMediaQuery('feed');
  const { t } = useTranslation('ui');
  const [showHFDisplay, setShowHFDisplay] = useState(false);

  // Active temporary visit (Change 3) — single boundary derived for feed-scope threading.
  // Deliberately NOT derived from/into selectedArea (addendum-1 §C).
  const visitBoundaryId = visitArea?.boundaryIds?.[0] || null;

  // Weather widget + left-column locality name, made visit-aware (2026-09-02 fix, §2).
  // Deliberately computed here rather than mutating App.js's userHomeLocationName/Coords state —
  // that state is re-derived from the true home row on every reload regardless (including visit
  // reloads), so it never needs an explicit "revert on visit end": once visitArea is null again,
  // these fall straight back to the real home values on the next render, same as visitBoundaryId
  // above and the header indicator text. Not threaded to ChatDetail (App.js) or
  // localityPublishCoords (AddEditArticle/WalkingComposer) — those read App.js state directly and
  // are out of this fix's scope; localityPublishCoords must stay home-only per addendum-1 §C.
  const effectiveHomeLocationName = visitArea?.displayName || userHomeLocationName;
  const effectiveHomeLocationCoords = (visitArea?.lat != null && visitArea?.lon != null)
    ? { lat: visitArea.lat, lon: visitArea.lon }
    : userHomeLocationCoords;

  // Mobile scroll tracking for floating footer buttons
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isNearEndOfContent, setIsNearEndOfContent] = useState(false);

  // frontPageActive / setFrontPageActive — lifted to App.js (survives MainFeed unmount/remount)

  // Help/Error report modal
  const [showErrorReportModal, setShowErrorReportModal] = useState(false);

  // Mobile sub-group / federation discovery was sourced here for the old mobile strips. Retired
  // in Mobile Groups (Commit 4) — MobileGroupFrontPage now reads childGroups / federationMembers
  // from useGroupHome (the same hook the desktop rail uses), so the bespoke MainFeed fetch is gone.

  // Debug: Track demo group changes
  useEffect(() => {
    if (selectedGroup) {
      console.log('🎭 MainFeed - selectedGroup changed:', {
        name: selectedGroup.name,
        id: selectedGroup.id,
        is_demo: selectedGroup.is_demo,
        demo_slug: selectedGroup.demo_slug
      });
    } else {
      console.log('🎭 MainFeed - selectedGroup cleared (null)');
    }
  }, [selectedGroup]);

  // Listen for H&F Display modal trigger
  useEffect(() => {
    const handleShowHFDisplay = () => {
      setShowHFDisplay(true);
    };

    window.addEventListener('ournet:showHFDisplay', handleShowHFDisplay);
    return () => window.removeEventListener('ournet:showHFDisplay', handleShowHFDisplay);
  }, []);

  // Listen for context switch events (from logo click)
  useEffect(() => {
    const handleContextSwitchEvent = (event) => {
      const { context } = event.detail;
      if (context) {
        handleContextSwitch(context);
      }
    };

    window.addEventListener('ournet:context-switch', handleContextSwitchEvent);
    return () => window.removeEventListener('ournet:context-switch', handleContextSwitchEvent);
  }, []);

  // Mobile: Track scroll position for floating footer button states
  useEffect(() => {
    if (!isMobile) return;

    const mainContent = document.getElementById('main-feed-content');
    if (!mainContent) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = mainContent;
      setScrollPosition(scrollTop);

      // Consider "near end" when:
      // 1. There's scrollable content (scrollHeight > clientHeight)
      // 2. User has scrolled down (scrollTop > 50)
      // 3. Within 100px of bottom
      const hasScrollableContent = scrollHeight > clientHeight;
      const hasScrolledDown = scrollTop > 50;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      setIsNearEndOfContent(hasScrollableContent && hasScrolledDown && distanceFromBottom < 100);
    };

    mainContent.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => mainContent.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Restore scroll position when returning from detail views (article, chat, event)
  useEffect(() => {
    if (shouldRestoreScroll && feedScrollPosition > 0) {
      const mainContent = document.getElementById('main-feed-content');
      if (mainContent) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          mainContent.scrollTo({
            top: feedScrollPosition,
            behavior: 'instant'
          });
          console.log('📜 Restored feed scroll position:', feedScrollPosition);
        });
      }
      // Signal that scroll has been restored
      if (onScrollRestored) {
        onScrollRestored();
      }
    }
  }, [shouldRestoreScroll, feedScrollPosition, onScrollRestored]);

  // Handle Go to Top action
  const handleGoToTop = () => {
    const mainContent = document.getElementById('main-feed-content');
    if (mainContent) {
      mainContent.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Context configuration
  const contexts = {
    locality: {
      id: 'locality',
      name: currentContext?.name || userHomeLocationName,
      type: 'location',
      color: theme?.primary || '#024B99'
    },
    groups: {
      id: 'groups',
      name: selectedGroup ? selectedGroup.name : 'My Groups',
      type: 'group',
      color: theme?.primary || '#BB4D9A'
    }
  };

  const context = contexts[currentContext] || contexts.locality;

  // Handle context switching
  const handleContextSwitch = (newContext) => {
    setCurrentContext(newContext);
    setSelectedGroup(null); // Reset selected group when switching contexts
    setCurrentMode('news'); // Always start with news mode
    setFrontPageActive(true); // Re-enable front page when switching context
    if (setMobileScreen) setMobileScreen('news'); // Reset mobile screen to News

    // Reset groups initial mode to 'joined' when manually switching contexts
    if (setGroupsInitialMode) {
      setGroupsInitialMode('joined');
    }

    // CRITICAL FIX: Clear articles/chats/events immediately to prevent stale data showing
    // The articles array is shared between contexts, so we must clear it when switching
    // to prevent locality articles from appearing in groups context (or vice versa)
    window.dispatchEvent(new CustomEvent('ournet:clear-content'));

    // Reload locality articles when switching to locality context
    if (newContext === 'locality' && onLoadLocalityArticles) {
      onLoadLocalityArticles();
    }
  };

  // Group Discovery state - groups context with no group selected
  const isGroupDiscovery = currentContext === 'groups' && !selectedGroup;

  // Locality desktop front page — footer hidden, content fills viewport
  const isLocalityFrontPage = currentContext === 'locality' && !isMobile && frontPageActive;

  // Mobile locality front page — four full-width screens, no bottom bar
  const isMobileLocalityFrontPage = currentContext === 'locality' && isMobile && frontPageActive;

  // ── Groups Tier 1: §4 routing-gate state ──────────────────────────────────
  // Resolve membership + published-landing for the selected group so the gate can route a
  // private non-member away from the interior (to LandingPageGroup or the minimal private gate)
  // on ALL viewports. setGateRefresh re-runs the resolve after a join from the gate.
  const { groupGate, setGateRefresh } = useGroupGate({ currentContext, selectedGroup, isLoggedIn, currentUser });

  // Is the selected group closing (Roadmap #103)? Read once here, handed to both group front pages.
  const groupClosing = useGroupClosing(currentContext === 'groups' ? selectedGroup?.id || null : null, currentUser);

  // A private group the viewer is NOT a confirmed member of: restricted on every viewport.
  // isMember starts false on every (re)check — viewer change or group change (Roadmap #100,
  // addendum 1 ruling 2) — so the interior never renders on another viewer's or group's answer.
  const groupRestricted = currentContext === 'groups' && !!selectedGroup
    && selectedGroup.is_private && !groupGate.isMember;

  // Restricted + a published landing exists → route to LandingPageGroup (App renders the
  // group-landing-view; MainFeed unmounts). Dispatched from an effect, never during render.
  useEffect(() => {
    if (groupRestricted && !groupGate.loading && groupGate.hasLanding && selectedGroup) {
      // fromGate flags this as the §4 redirect (private non-member). App routes the landing
      // page's Back to the previous group's Home in this case — without it, Back would leave
      // selectedGroup on the gated group and the gate would immediately re-fire (a loop).
      window.dispatchEvent(new CustomEvent('ournet:navigate', {
        detail: { view: 'group-landing-view', group: selectedGroup, fromGate: true }
      }));
    }
  }, [groupRestricted, groupGate.loading, groupGate.hasLanding, selectedGroup?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Membership-aware landing tab (mobile). The mobile group tab is driven by currentMode
  // (MobileGroupFrontPage activeScreen={currentMode}). The rule: a MEMBER (owner counts) lands on
  // News — the content, matching locality's news-first behaviour; a NON-MEMBER of a public group
  // lands on Home — the info surface for someone still deciding. Private non-members never reach a
  // tab (the §4 gate routes them to the landing page / GroupPrivateGate above).
  //
  // Driven by the gate's already-resolved membership (groupGate.isMember — the same derivation the
  // gate uses), so it covers EVERY entry path that funnels through selectedGroup (handleGroupSelect,
  // smart links, hash nav, invite-accept, gate Back, landing "continue") with one rule, plus the
  // gate-RELEASE case: a non-member who JUST joined (isMember flips false→true via gateRefresh) is
  // rewarded with News. Keyed on gate state, NOT currentMode, so a user's own tab taps are preserved
  // (tapping a tab changes currentMode but none of this effect's deps, so it does not re-fire).
  // Desktop is intentionally excluded: the rail always shows the Home content and News is the main
  // column, so currentMode is irrelevant there — leaving the prior desktop behaviour untouched.
  const landingTabRef = useRef({ groupId: null, wasMember: null });
  useEffect(() => {
    if (!isMobile) return;
    if (currentContext !== 'groups' || !selectedGroup?.id) {
      landingTabRef.current = { groupId: null, wasMember: null };
      return;
    }
    if (isDemoMode) return;              // demos showcase content (News) — outside the membership model
    if (groupGate.loading) return;       // wait for the membership truth before choosing the tab

    const gid = selectedGroup.id;
    const isMember = groupGate.isMember;
    const prev = landingTabRef.current;

    if (prev.groupId !== gid) {
      // Fresh entry into this group → land by membership.
      setCurrentMode(isMember ? 'news' : 'home');
      landingTabRef.current = { groupId: gid, wasMember: isMember };
    } else if (prev.wasMember === false && isMember === true) {
      // Gate-release: was a non-member here, just joined → reward with the content.
      setCurrentMode('news');
      landingTabRef.current = { groupId: gid, wasMember: true };
    } else {
      landingTabRef.current = { groupId: gid, wasMember: isMember };
    }
  }, [isMobile, currentContext, selectedGroup?.id, isDemoMode, groupGate.loading, groupGate.isMember]); // eslint-disable-line react-hooks/exhaustive-deps

  // Desktop group front page (member/public). Layout twin of isLocalityFrontPage: no fixed
  // footer (branding lives in the rail), floating help, marginBottom 0. Covers the desktop
  // gate screen too (still a full-page, footer-less surface).
  const isGroupFrontPage = currentContext === 'groups' && !isMobile && frontPageActive && !!selectedGroup;

  // Mobile group front page (member/public). The mobile twin of isGroupFrontPage: MobileGroupFrontPage
  // owns the identity card + framed tabs + sheet. False when restricted — the §4 gate renders instead.
  const isMobileGroupFrontPage = currentContext === 'groups' && isMobile && frontPageActive && !!selectedGroup && !groupRestricted;

  // Handle back from Group Discovery - return to locality feed
  const handleBackFromGroupDiscovery = () => {
    handleContextSwitch('locality');
  };

  // Context toggle relocated to the group header top-left (Mobile Groups, Commit 5) — the bottom
  // bar no longer carries it, so handleContextToggle is retired. The header calls onContextSwitch.

  // Handle mode switching from sidebar or front page
  const handleModeSwitch = (mode) => {
    setCurrentMode(mode);
    setFrontPageActive(false);
  };

  // Handle group selection
  const handleGroupSelect = (group) => {
    console.log('Group selected in MainFeed:', group);
    // Tier 2: remember where we came from so the §4 gate's Back can return there. Capturing the
    // OUTGOING selectedGroup before switching gives one-level "back to the previous group's Home".
    setPreviousGroup?.(selectedGroup || null);
    // Selecting a group always means Groups context. Existing callers (GroupsGrid,
    // GroupFrontPage/MobileGroupFrontPage sub-group nav) are already in 'groups' context, so
    // this is a no-op re-render for them — it only matters for callers reached from Locality
    // (the front-page GroupsColumn). Deliberately NOT handleContextSwitch: that also resets
    // selectedGroup to null, which would race with the setSelectedGroup(group) below.
    setCurrentContext('groups');
    setSelectedGroup(group);
    setCurrentMode('home'); // provisional (non-member default); the membership-aware effect above
                            // refines this on mobile once the gate resolves (member → News)
    setFrontPageActive?.(true); // Entering a group lands on its front page (mobile: MobileGroupFrontPage)

    // CRITICAL FIX: Clear content arrays immediately before loading new group data
    // Prevents previous group's articles from appearing while new group loads
    window.dispatchEvent(new CustomEvent('ournet:clear-content'));

    // Load articles for the newly selected group
    if (onLoadArticlesForGroup && group?.id) {
      onLoadArticlesForGroup(group.id);
    }

    // Load chats for the newly selected group
    if (onLoadChatsForGroup && group?.id) {
      onLoadChatsForGroup(group.id);
    }

    // Load events for the newly selected group
    if (onLoadEventsForGroup && group?.id) {
      onLoadEventsForGroup(group.id);
    }
  };

  // Render content grid based on current mode
  const renderContentGrid = () => {
    // Locality front page — desktop or mobile
    // frontPageActive is false when user explicitly switches mode (e.g. NewsBar → NewsGrid)
    if (currentContext === 'locality' && frontPageActive) {
      if (isMobile) {
        return (
          <MobileFrontPage
            articles={articles}
            feedPaging={feedPaging}
            chats={chats}
            events={events}
            onArticleClick={onArticleClick}
            onChatClick={onChatClick}
            onEventClick={onEventClick}
            mobileScreen={mobileScreen}
            openChatThreadId={openChatThreadId}
            onChatThreadToggle={onChatThreadToggle}
            selectedArea={selectedArea}
            onChatsRefresh={onChatsRefresh}
            userHomeLocationName={effectiveHomeLocationName}
            userHomeLocationCoords={effectiveHomeLocationCoords}
            currentUser={currentUser}
            sessionReady={sessionReady}
            onGroupSelect={handleGroupSelect}
            guestFallbackBoundaryId={detectedBoundaryId}
            visitBoundaryId={visitBoundaryId}
          />
        );
      }
      return (
        <LocalityFrontPage
          articles={articles}
          newsHasMore={articlesHasMore}
          feedPaging={feedPaging}
          chats={chats}
          events={events}
          onArticleClick={onArticleClick}
          onChatClick={onChatClick}
          onEventClick={onEventClick}
          onModeSwitch={handleModeSwitch}
          openChatThreadId={openChatThreadId}
          onChatThreadToggle={onChatThreadToggle}
          selectedArea={selectedArea}
          onChatsRefresh={onChatsRefresh}
          currentUser={currentUser}
          sessionReady={sessionReady}
          onGroupSelect={handleGroupSelect}
          guestFallbackBoundaryId={detectedBoundaryId}
          userHomeLocationName={effectiveHomeLocationName}
          userHomeLocationCoords={effectiveHomeLocationCoords}
          visitBoundaryId={visitBoundaryId}
        />
      );
    }

    // Groups context: Show GroupsGrid if no group selected
    if (currentContext === 'groups' && !selectedGroup) {
      return (
        <GroupsGrid
          onGroupSelect={handleGroupSelect}
          currentUser={currentUser}
          sessionReady={sessionReady}
          initialMode={groupsInitialMode}
          selectedArea={selectedArea}
        />
      );
    }

    // Groups context with a selected group — §4 routing gate (all viewports). Replaces the old
    // logged-out-only placeholder, which leaked the interior to logged-in non-members and was
    // entry-path-dependent. A private non-member NEVER reaches the interior here.
    if (currentContext === 'groups' && selectedGroup) {
      if (groupRestricted) {
        // Still resolving, OR a published landing exists (the redirect effect navigates to
        // group-landing-view) → neutral placeholder; never the interior.
        if (groupGate.loading || groupGate.hasLanding) {
          return <div style={{ flex: 1 }} />;
        }
        // No landing page → minimal private gate (name + private + join CTA, no interior).
        return (
          <GroupPrivateGate
            group={selectedGroup}
            currentUser={currentUser}
            isLoggedIn={isLoggedIn}
            onShowLoginModal={onShowLoginModal}
            onMembershipChange={() => setGateRefresh(n => n + 1)}
            onBack={onGroupGateBack}
          />
        );
      }

      // Member or public group on MOBILE → the mobile group front page (Approach B). Tabs switch
      // content within the front page, so onScreenChange only sets currentMode (it must NOT clear
      // frontPageActive — that would exit the front page to a bare mode grid).
      if (isMobile && frontPageActive) {
        return (
          <MobileGroupFrontPage
            group={selectedGroup}
            hideSearch={isDemoMode}
            articles={articles}
            chats={chats}
            events={events}
            onArticleClick={onArticleClick}
            onChatClick={onChatClick}
            onEventClick={onEventClick}
            openChatThreadId={openChatThreadId}
            onChatThreadToggle={onChatThreadToggle}
            onChatsRefresh={onChatsRefresh}
            currentUser={currentUser}
            onGroupSelect={handleGroupSelect}
            onMembershipChange={() => setGateRefresh(n => n + 1)}
            canWriteChat={groupGate.isMember}
            groupClosing={groupClosing}
            activeScreen={currentMode}
            onScreenChange={(screen) => setCurrentMode(screen)}
          />
        );
      }

      // Member or public group → desktop front page when the front page is active.
      if (!isMobile && frontPageActive) {
        return (
          <GroupFrontPage
            group={selectedGroup}
            articles={articles}
            newsHasMore={articlesHasMore}
            chats={chats}
            events={events}
            onArticleClick={onArticleClick}
            onChatClick={onChatClick}
            onEventClick={onEventClick}
            onModeSwitch={handleModeSwitch}
            openChatThreadId={openChatThreadId}
            onChatThreadToggle={onChatThreadToggle}
            onChatsRefresh={onChatsRefresh}
            currentUser={currentUser}
            onGroupSelect={handleGroupSelect}
            onMembershipChange={() => setGateRefresh(n => n + 1)}
            canWriteChat={groupGate.isMember}
            groupClosing={groupClosing}
          />
        );
      }
      // else: fall through to the mode-grid switch (desktop after NewsBar click, and mobile).
    }

    // "Back to front page" button — shown on locality desktop when user drilled into a mode grid
    const backToFrontPage = !isMobile && !frontPageActive && (currentContext === 'locality' || currentContext === 'groups') ? (
      <div style={{ padding: '12px 16px', flexShrink: 0 }}>
        <StandardButton
          variant="context"
          iconName="ArrowLeft"
          label={t('frontpage:back_to_front_page')}
          onClick={() => setFrontPageActive(true)}
        />
      </div>
    ) : null;

    // Render appropriate grid based on current mode
    switch (currentMode) {
      case 'news':
        return (
          <>
            {backToFrontPage}
            <NewsGrid
              articles={articles.filter(a => a.article_type === 'news' || a.article_type === 'website')}
              paging={currentContext === 'locality' ? feedPaging?.news : null}
              pagingBusy={Boolean(feedPaging?.busy)}
              onArticleClick={onArticleClick}
              currentContext={context}
              isMobile={isMobile}
              selectedGroup={currentContext === 'groups' ? selectedGroup : null}
            />
          </>
        );
      case 'chat':
        return (
          <>
            {backToFrontPage}
            <ChatGrid
              chats={chats}
              onChatClick={onChatClick}
              onNewChat={onNewChat}
              currentContext={context}
              isMobile={isMobile}
              selectedGroup={currentContext === 'groups' ? selectedGroup : null}
            />
          </>
        );
      case 'events':
        return (
          <>
            {backToFrontPage}
            <EventsGrid
              events={events}
              onEventClick={onEventClick}
              currentContext={context}
              userId={currentUser?.id}
              isMobile={isMobile}
              selectedGroup={currentContext === 'groups' ? selectedGroup : null}
            />
          </>
        );
      default:
        return (
          <>
            {backToFrontPage}
            <NewsGrid
              articles={articles.filter(a => a.article_type === 'news' || a.article_type === 'website')}
              onArticleClick={onArticleClick}
              currentContext={context}
              isMobile={isMobile}
              selectedGroup={currentContext === 'groups' ? selectedGroup : null}
            />
          </>
        );
    }
  };

  return (
    <div
      className="app-shell"
      style={{
        // Shell locked to the viewport — the window itself never scrolls. Each content
        // column (sidebar bottom section, #main-feed-content, chat list) owns its own
        // scrollbar. Height now comes from the .app-shell class (100dvh, 100vh fallback)
        // so the shell tracks the live visible viewport instead of the larger
        // address-bar-hidden viewport that left a residual window scrollbar.
        overflow: 'hidden',
        background: theme.bg,
        display: 'flex',
        flexDirection: 'column'
      }}>
      {/* HEADER SECTION */}
      {isGroupDiscovery ? (
        // Group Discovery header - mobile: compact 50px, desktop: HeaderContainer + HeaderButtonsForm
        isMobile ? (
          <header style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '50px',
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            padding: '0 12px',
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            gap: '12px'
          }}>
            <button
              onClick={handleBackFromGroupDiscovery}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ArrowLeft size={24} color="#BB4D9A" />
            </button>
            <span style={{
              fontSize: '15px',
              fontWeight: 'bold',
              color: '#BB4D9A',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1
            }}>
              {t('group_discovery')}
            </span>
          </header>
        ) : (
          <>
            <HeaderContainer />
            <HeaderButtonsForm
              currentContext="groups"
              onBack={handleBackFromGroupDiscovery}
              headerTitle={t('group_discovery')}
              showGroupSearch={true}
            />
          </>
        )
      ) : (
        // Standard feed header - layered architecture (siblings)
        <>
          <HeaderContainer />
          <HeaderButtonsFeed
            hideSearch={isDemoMode}
            currentContext={context}
            currentMode={currentMode}
            selectedGroup={selectedGroup}
            selectedArea={selectedArea}
            setSelectedArea={setSelectedArea}
            onContextSwitch={handleContextSwitch}
            onGuestLocationChange={onGuestLocationChange}
            frontPageActive={frontPageActive}
            mobileScreen={mobileScreen}
            setMobileScreen={setMobileScreen}
            visitArea={visitArea}
            onEnterVisit={onEnterVisit}
            onClearVisit={onClearVisit}
          />
        </>
      )}

      {/* MAIN CONTENT SECTION */}
      <div style={{
        display: 'flex',
        flex: 1,
        // min-height:0 / min-width:0 let this flex row shrink to the shell instead of
        // growing to its tallest child (the sidebar) and pushing a window scrollbar back in.
        minHeight: 0,
        minWidth: 0,
        // The header is position:fixed (out of flow), so the row only needs a top margin to
        // clear it; flex:1 then fills the rest of the 100vh shell. Footer space (desktop,
        // non-front-page) is reserved with marginBottom. No explicit calc(100vh - Npx) height:
        // it double-counted the offset against the margin and produced a few-pixel window
        // scrollbar while resizing. Let flex size the row from the shell instead.
        // headerHeight/footerHeight (useMediaQuery('feed')) supply the standard two cases —
        // desktop 80px and the mobile two-row feed header 90px. The other mobile branches are
        // genuine one-off chrome shapes (front-page extras, single-row group/discovery headers)
        // that fall outside the base feed/detail matrix, so they stay local literals here.
        marginTop: isMobile
          ? (isMobileLocalityFrontPage ? '94px'  // Row 1 (50) + tab bar (44) — Row 3 retired, mobile-tab-subheaders brief
            : isMobileGroupFrontPage ? '50px'  // plain Row-1 header; the create action line sits in the body, under the tabs
            : (currentContext === 'groups' && !selectedGroup) ? '50px'  // group discovery uses the compact 50px header; GroupsGrid reads the same headerHeight now, so this can't drift out of sync again
            : (currentMode === 'news' || currentMode === 'events' || currentMode === 'chat' ? `${headerHeight}px` : '50px'))
          : `${headerHeight}px`,
        marginBottom: isMobile ? '0' : ((isLocalityFrontPage || isGroupFrontPage) ? '0' : `${footerHeight}px`)
      }}>
        {/* LEFT SIDEBAR - Desktop, LOCALITY only. The locality front page retires it (content
            moved to the Directory Mode Column); non-front-page locality mode grids still render
            it. Groups no longer have a left sidebar — SidebarGroups was retired in Tier 1; the
            group rail now lives in GroupFrontPage's GroupHomeColumn. */}
        {!isMobile && currentContext === 'locality' && !isLocalityFrontPage && (
          <SidebarLocality
            currentMode={currentMode}
            onModeSwitch={handleModeSwitch}
            articles={articles}
            chats={chats}
            events={events}
            userHomeLocationName={effectiveHomeLocationName}
            userHomeLocationCoords={effectiveHomeLocationCoords}
            hasFooterOverlay={isLocalityFrontPage}
            selectedArea={selectedArea}
            currentUser={currentUser}
            guestFallbackBoundaryId={detectedBoundaryId}
            visitBoundaryId={visitBoundaryId}
          />
        )}

        {/* MAIN CONTENT AREA */}
        <div id="main-feed-content" style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          background: theme.bg,
          padding: '0',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}>
          {/* Mobile sub-group / federation strips removed (Mobile Groups, Commit 4) — the hierarchy
              now lives on MobileGroupFrontPage's Home tab (GroupHomePanel), sourced from useGroupHome. */}
          {renderContentGrid()}
        </div>
      </div>

      {/* FOOTER SECTION - Responsive */}
      {isMobile ? (
        !isGroupDiscovery && !isMobileLocalityFrontPage && !groupRestricted && (
          <MobileBottomBar
            currentContext={currentContext}
            scrollPosition={scrollPosition}
            isNearEndOfContent={isNearEndOfContent}
            onGoToTop={handleGoToTop}
            onHelpPress={() => setShowErrorReportModal(true)}
          />
        )
      ) : (isLocalityFrontPage || isGroupFrontPage) ? (
        /* Front page (locality or group) renders no fixed footer — branding lives at the bottom
           of the column-1 rail. The floating Help "?" (below) remains for the content area. */
        null
      ) : (
        !isGroupDiscovery && (
          <FooterContainer currentView={currentMode} currentContext={context}>
            <FooterButtonsFeed
              currentContext={context}
              viewType="feed"
              currentView={currentMode}
            />
          </FooterContainer>
        )
      )}

      {/* Floating Help button — Locality front page (desktop + mobile) where the footer/bottom
          bar are hidden. On mobile it rehomes the Help that lived in the retired sidebar panel. */}
      {(isLocalityFrontPage || isMobileLocalityFrontPage || isGroupFrontPage) && (
        <div style={{
          position: 'fixed',
          bottom: isMobile ? '16px' : '40px',
          right: isMobile ? '16px' : '40px',
          zIndex: 1100
        }}>
          <StandardButton
            variant="icon"
            text="?"
            color="#F9A61B"
            title={t('please_help')}
            onClick={() => setShowErrorReportModal(true)}
          />
        </div>
      )}

      {/* Mobile NavigationDrawer + SwipeEdgeHint removed (Mobile Groups, Commit 4). The mobile
          locality front page never used them; mobile groups now use MobileGroupFrontPage's
          identity card + bottom sheet, opened from the card's "Group" button. */}

      {/* Header & Footer Display Modal */}
      <HeaderFooterDisplay
        isOpen={showHFDisplay}
        onClose={() => setShowHFDisplay(false)}
      />

      {/* Error Report / Help Modal */}
      {showErrorReportModal && (
        <ErrorReportModal
          isOpen={showErrorReportModal}
          onClose={() => setShowErrorReportModal(false)}
          currentView={currentMode}
          currentContext={{
            name: currentContext === 'locality' ? userHomeLocationName : (selectedGroup?.name || 'Groups'),
            type: currentContext === 'locality' ? 'location' : 'group',
            component: 'MainFeed',
            selectedArea: selectedArea?.display_name || selectedArea?.area_code || null,
            groupId: selectedGroup?.id || null,
            itemType: currentMode
          }}
        />
      )}

      {/* DEMO WATERMARK - shown when viewing a demo group */}
      {showDemoWatermark && <DemoWatermark />}
    </div>
  );
}

export default MainFeed;
